import {getLibraryQueue,subscribeLibrarySync} from '../utils/librarySync';
import {fetchWithAuth} from '../services/api';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getWishlist, addToWishlist as addToWishlistApi, removeFromWishlist as removeFromWishlistApi, updateWishlistItem as updateWishlistItemApi } from '../services/api';
import { useUser } from './UserContext';
import { pendingRemovals, restoreRemovedItem } from '../utils/pendingRemovals';
import { showToast, dismissToast } from '../components/GlobalToast';
import { readOffline, writeOffline, updateOffline } from '../utils/offlineCache';
import i18n from '../i18n';

const WishlistContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountWishlistProvider key={user?.id ?? 'guest'} userId={user?.id}>{children}</AccountWishlistProvider>;
};

const AccountWishlistProvider = ({ children, userId }) => {
  const [wishlistItems, setWishlistItems] = useState(() => readOffline('wishlist', userId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(()=>subscribeLibrarySync(()=>{
    const q=getLibraryQueue(fetchWithAuth);if(!q || q.getState().userId!==userId)return;
    const rows=q.snapshot('wishlist');
    setWishlistItems(rows.filter(item=>!pendingRemovals.has(`${userId}:wishlist:${item.category}:${item.external_id}`)));
  }),[userId]);

  const loadWishlist = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getWishlist();
      const parsed = (data || []).map(item => {
        if (item.genres && typeof item.genres === 'string') {
          try { item.genres = JSON.parse(item.genres); } catch { item.genres = []; }
        }
        return item;
      });
      setWishlistItems(parsed.filter(item => !pendingRemovals.has(`${userId}:wishlist:${item.category}:${item.external_id}`)));
      writeOffline('wishlist', userId, parsed);
    } catch (error) {
      console.error('Ошибка загрузки вишлиста:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const refreshWishlist = useCallback(async () => {
    await loadWishlist();
  }, [loadWishlist]);

  // Загружаем данные с бэкэнда при инициализации или входе/выходе пользователя
  useEffect(() => {
    if (userId) {
      loadWishlist();
    } else {
      setWishlistItems([]);
    }
  }, [userId, loadWishlist]);
  useEffect(() => {
    window.addEventListener('online', loadWishlist);
    return () => window.removeEventListener('online', loadWishlist);
  }, [loadWishlist]);

  const addToWishlist = async (item, userRating = null) => {
    try {
      const restored = pendingRemovals.undo(`${userId}:wishlist:${item.category}:${item.external_id ?? item.id}`);
      if (restored) return userRating == null ? restored : updateWishlistItem(restored.id, restored.category, { user_rating: userRating });
      const genres = item.genres || [];

      const newItem = await addToWishlistApi({
        external_id: item.external_id ?? item.id,
        category: item.category,
        title: item.title,
        original_title: item.originalTitle,
        overview: item.overview,
        poster: item.category !== 'music' ? item.poster : undefined,
        artwork: item.category === 'music' ? item.artwork : undefined,
        backdrop: item.backdrop,
        rating: item.rating,
        release_date: item.releaseDate,
        priority: '',
        ...(userRating != null ? { user_rating: userRating } : {}),
        genres: genres
      });

      // Parse genres for the new item (same as in loadWishlist)
      if (newItem && newItem.genres && typeof newItem.genres === 'string') {
        try { newItem.genres = JSON.parse(newItem.genres); } catch { newItem.genres = []; }
      }

      setWishlistItems(prev => [...prev.filter(row => row.id !== newItem.id), newItem]);
      updateOffline('wishlist', userId, rows => [...rows.filter(row => row.id !== newItem.id), newItem]);
      return newItem;
    } catch (error) {
      // Only a duplicate is recoverable, and only with the real database row.
      if (error?.message?.includes('уже в вашем вишлисте')) {
        const items = await getWishlist();
        const existingItem = items.find(row => String(row.external_id) === String(item.id ?? item.external_id) && row.category === item.category);
        if (existingItem) {
          setWishlistItems(items);
          return existingItem;
        }
      }
      console.error('Ошибка добавления в вишлист:', error);
      throw error;
    }
  };

  const removeFromWishlist = async (itemId, category) => {
    try {
      // Находим элемент по external_id и category, чтобы получить его id
      const item = wishlistItems.find(
        item => String(item.external_id) === String(itemId) && item.category === category
      );

      if (!item) {
        throw new Error('Элемент не найден в вишлисте');
      }

      const key = `${userId}:wishlist:${item.category}:${item.external_id}`;
      const token = localStorage.getItem('polka_token');
      const restore = () => setWishlistItems(prev => restoreRemovedItem(prev, item, wishlistItems));
      const toast = showToast({ title: i18n.t('ux.removedWishlist'), message: item.title, duration: 8000,
        action: { label: i18n.t('ux.undo'), onClick: () => pendingRemovals.undo(key) } });
      pendingRemovals.add(key, {
        restore, value: item,
        commit: async keepalive => {
          await removeFromWishlistApi(item.id, { keepalive, headers: { Authorization: `Bearer ${token}` } });
          updateOffline('wishlist', userId, rows => rows.filter(row => row.id !== item.id));
        },
        failed: () => showToast({ title: i18n.t('ux.deleteFailed'), message: i18n.t('ux.deleteRestored'), type: 'error' }),
        finished: () => dismissToast(toast)
      });
      setWishlistItems(prev =>
        prev.filter(item => !(String(item.external_id) === String(itemId) && item.category === category))
      );
    } catch (error) {
      console.error('Ошибка удаления из вишлиста:', error);
      throw error;
    }
  };

  const isInWishlist = (itemId, category) => {
    if (!wishlistItems || wishlistItems.length === 0) return false;
    return wishlistItems.some(
      item => item && String(item.external_id) === String(itemId) && item.category === category
    );
  };

  const updateWishlistItem = async (itemId, category, updates) => {
    try {
      const updatedItem = await updateWishlistItemApi(itemId, updates);
      updateOffline('wishlist', userId, rows => rows.map(row => String(row.id) === String(itemId) ? updatedItem : row));
      setWishlistItems(prev => prev.map(row =>
        String(row.id) === String(itemId) && row.category === category ? updatedItem : row
      ));

      return updatedItem;
    } catch (error) {
      console.error('Ошибка обновления элемента вишлиста:', error);
      throw error;
    }
  };

  const getWishlistByCategory = (category) => {
    if (category === 'all') {
      return wishlistItems;
    }
    return wishlistItems.filter(item => item.category === category);
  };

  const getStats = () => {
    return {
      total: wishlistItems.length,
      films: wishlistItems.filter(i => i.category === 'film').length,
      series: wishlistItems.filter(i => i.category === 'series').length,
      games: wishlistItems.filter(i => i.category === 'game').length,
      music: wishlistItems.filter(i => i.category === 'music').length,
      books: wishlistItems.filter(i => i.category === 'book').length
    };
  };

  const getPriorityStats = () => {
    return {
      noPriority: wishlistItems.filter(i => !i.priority || i.priority === '').length,
      high: wishlistItems.filter(i => i.priority === 'high').length,
      medium: wishlistItems.filter(i => i.priority === 'medium').length,
      low: wishlistItems.filter(i => i.priority === 'low').length
    };
  };

  const getFilteredByPriority = (priority) => {
    if (!priority) {
      return wishlistItems;
    }
    if (priority === 'noPriority') {
      return wishlistItems.filter(i => !i.priority || i.priority === '');
    }
    return wishlistItems.filter(i => i.priority === priority);
  };

  const reorderWishlist = (sourceIndex, destIndex) => {
    setWishlistItems(prev => {
      const result = Array.from(prev);
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(destIndex, 0, removed);
      return result;
    });
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      loading,
      error,
      refreshWishlist,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      updateWishlistItem,
      getWishlistByCategory,
      getStats,
      getPriorityStats,
      getFilteredByPriority,
      reorderWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
